import { translateActionUrl } from '@/components/notifications/notification-url-utils';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export type DesktopLifecycleEvent = 'focus' | 'resume';

export const DESKTOP_BRIDGE_API_VERSION = 2;

// Every member added after version 1 must stay optional and be checked before use.
const ACCEPTED_DESKTOP_BRIDGE_API_VERSIONS: ReadonlySet<unknown> = new Set([
  1,
  DESKTOP_BRIDGE_API_VERSION,
]);

export type DesktopAuthCallback =
  | { readonly kind: 'code'; readonly code: string }
  | { readonly kind: 'error'; readonly reason: 'cancelled' | 'failed' };

export interface DesktopOAuthBrowserBinding {
  readonly correlation: string;
  readonly browserBinding: string;
}

export interface DesktopNotificationRequest {
  id: string;
  actionPath: string;
  silent: boolean;
}

export interface DesktopNavigationRequest {
  path: string;
  notificationId: string;
}

export type DesktopThemePreference = 'system' | 'light' | 'dark';

export interface DesktopChromeState {
  readonly themePreference: DesktopThemePreference;
  readonly isDarkMode: boolean;
  readonly isFocusMode: boolean;
  readonly zoom: number;
  readonly supportsNavigationHistory: boolean;
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
  readonly hasRecentHistory: boolean;
}

export type DesktopChromeCommand =
  | { readonly type: 'history-back' }
  | { readonly type: 'history-forward' }
  | { readonly type: 'history-open' }
  | { readonly type: 'open-notifications' }
  | { readonly type: 'set-theme'; readonly value: DesktopThemePreference }
  | { readonly type: 'set-zoom'; readonly value: number }
  | { readonly type: 'toggle-focus-mode' };

export interface PublicPulseDesktopBridge {
  readonly isDesktop: true;
  readonly bridgeApiVersion: number;
  readonly authCallbackUrl: string;
  readonly authStorage: {
    getItem: (key: string) => Promise<string | null>;
    setItem: (key: string, value: string) => Promise<void>;
    removeItem: (key: string) => Promise<void>;
  };
  openOAuth: (url: string) => Promise<boolean>;
  showNotification: (request: DesktopNotificationRequest) => void;
  setBadgeCount: (count: number) => void;
  readonly chrome?: {
    setState: (state: DesktopChromeState) => void;
    setReady: (ready: boolean) => void;
    onCommand: (callback: (command: DesktopChromeCommand) => void) => () => void;
  };
  onAuthCallback: (callback: (payload: DesktopAuthCallback) => void) => () => void;
  onOAuthBrowserBinding?: (callback: (payload: DesktopOAuthBrowserBinding) => void) => () => void;
  onNavigate: (callback: (request: DesktopNavigationRequest) => void) => () => void;
  onLifecycle: (callback: (event: DesktopLifecycleEvent) => void) => () => void;
}

declare global {
  interface Window {
    publicPulseDesktop?: PublicPulseDesktopBridge;
  }
}

export interface DesktopNotificationPreferences {
  dnd_enabled: boolean;
  dnd_until: string | null;
  sound_enabled: boolean;
}

export interface DesktopNotificationInsert {
  id: string;
  actionPath: string;
}

const NOTIFICATIONS_PATH = '/v2/notifications';

export function getDesktopBridge(): PublicPulseDesktopBridge | null {
  if (globalThis.window === undefined) {
    return null;
  }
  const bridge = globalThis.window.publicPulseDesktop;
  if (
    bridge?.isDesktop !== true ||
    !ACCEPTED_DESKTOP_BRIDGE_API_VERSIONS.has(bridge.bridgeApiVersion)
  ) {
    return null;
  }
  return bridge;
}

export function isDesktopPlatform(): boolean {
  return (
    globalThis.window !== undefined && globalThis.window.publicPulseDesktop?.isDesktop === true
  );
}

export function isDesktopBridgeCompatible(): boolean {
  return getDesktopBridge() !== null;
}

export function isDesktopDndActive(
  preferences: DesktopNotificationPreferences | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!preferences?.dnd_enabled) {
    return false;
  }
  if (!preferences.dnd_until) {
    return true;
  }
  const until = Date.parse(preferences.dnd_until);
  return Number.isNaN(until) || until > now;
}

export function resolveDesktopNotificationPath(actionUrl: unknown): string {
  if (typeof actionUrl !== 'string' || !actionUrl.startsWith('/')) {
    return NOTIFICATIONS_PATH;
  }
  return translateActionUrl(actionUrl) ?? NOTIFICATIONS_PATH;
}

export function parseDesktopNotificationInsert(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  organizationId: string | null,
  userId: string | undefined,
): DesktopNotificationInsert | null {
  if (payload.eventType !== 'INSERT' || !organizationId || !userId) {
    return null;
  }
  const record = payload.new;
  if (
    typeof record.id !== 'string' ||
    record.organization_id !== organizationId ||
    record.recipient_user_id !== userId
  ) {
    return null;
  }
  return {
    id: record.id,
    actionPath: resolveDesktopNotificationPath(record.action_url),
  };
}
